import { businessShape, deskMay, sessionFill, type BusinessSession, type MyBusiness } from "@teamna/shared";
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { track } from "@teamna/web-client/analytics/web.ts";
import { formatDayLong, formatTime } from "@teamna/web-client/lib/format.ts";
import { AvatarName, Button, ErrorState, Panel } from "../../components/index.ts";
import { useOwnerSessions } from "@teamna/web-client/hooks/use-owner.ts";
import { useTimetableEditor } from "@teamna/web-client/hooks/use-timetable.ts";
import { sessionColor } from "../../lib/group-color.ts";
import { canTakeAttendance, todayProgress, todayStatusText, type TodayProgress } from "../today/progress.ts";
import { SessionPanelContent } from "../today/SessionPanel.tsx";
import { TodayChips } from "../today/TodayChips.tsx";
import { TodayStale } from "../today/TodayStale.tsx";
import { useScheduleWords } from "../schedule-grid.tsx";
import { useStaffPhotos } from "../staff-photos.ts";
import { useTodaySessions } from "../today/use-today.ts";
import { FillMeter } from "./fill-meter.tsx";

/**
 * The "Sessions today" card on owner Home (2026-10-19): today's sessions in time order, with where
 * attendance is for each and Open session, which opens the session in a side panel over Home: the
 * same panel as a click on the Calendar's Day view (`SessionPanelContent`), with Take attendance at
 * its bottom. It used to open the Calendar; on a phone, Back then landed on the Calendar and the way
 * back to Home was not clear (pilot feedback, 2026-10-22). Closing the panel leaves the owner on Home.
 *
 * A line marks the time now. The card is left out on a day with no sessions.
 *
 * Each row shows how full the session is (Studio Home H1, every shape): booked of the places with a
 * bar, the waiting list and the first visits (`FillMeter`). Where clients pick each class the fill
 * comes first in the row; where each participant has a fixed group, attendance comes first. A click
 * anywhere on a row that runs opens the session with its class list.
 */

type Row = { session: BusinessSession; progress: TodayProgress; color: string };
type Slot = { startsAt: string; rows: Row[] };

const TONE: Record<TodayProgress["state"], string> = {
  taken: "text-confirmed-ink",
  missed: "text-late-ink",
  partial: "text-accent-strong",
  now: "text-accent-strong",
  later: "text-ink-2",
  empty: "text-ink-2",
  cancelled: "text-ink-2",
};

/** The button is the main action while attendance waits; a secondary one when it is done or later. */
const URGENT: ReadonlySet<TodayProgress["state"]> = new Set(["now", "partial", "missed"]);

function slotWhen(slot: Slot, now: number): string {
  const start = Date.parse(slot.startsAt);
  if (slot.rows.every((row) => Date.parse(row.session.endsAt) <= now)) return "Ended";
  if (start <= now) return "Now";
  const minutes = Math.ceil((start - now) / 60_000);
  return minutes < 60 ? `In ${minutes} min` : "Later";
}

export function TodayCard({ business }: { business: MyBusiness }) {
  const businessId = business.businessId;
  const timezone = business.timezone;
  const navigate = useNavigate();
  const { query, sessions, now, range } = useTodaySessions(businessId, timezone);
  // The same sessions as the Calendar reads them, for the panel's session rail.
  const owned = useOwnerSessions({ businessId, from: range.from, to: range.to });
  // The session in the side panel, and whether Take attendance is on screen in it.
  const [openId, setOpenId] = useState<string | null>(null);
  const [attending, setAttending] = useState(false);
  // The levels' colours, so a session has the same colour here as on the Calendar.
  // The front desk (T1) does not read the editor: the default colours.
  const editor = useTimetableEditor(deskMay(business.staffRole, "readTimetableEditor", business.frontDesk) ? businessId : null);
  const photoOf = useStaffPhotos(businessId);
  const words = useScheduleWords();
  const fillFirst = businessShape(business).classFill === "pick_class";

  if (!query.data) {
    if (query.isLoading) return null;
    return (
      <section aria-label="Sessions today" className="rounded-card border border-line bg-paper px-5 py-4">
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </section>
    );
  }
  if (sessions.length === 0) {
    // An empty day from a read that failed since: it can be out of date, so say so.
    if (!query.isError) return null;
    return (
      <section aria-label="Sessions today" className="flex flex-wrap items-center gap-3 rounded-card border border-line bg-paper px-5 py-3.5">
        <b className="text-[14.5px]">Sessions today:</b>
        <TodayStale onRetry={() => void query.refetch()} />
      </section>
    );
  }

  const rows: Row[] = sessions.map((session, index) => ({
    session,
    progress: todayProgress(session, now),
    color: sessionColor(editor.data?.groups ?? [], session.groupId, session.groupColor, index),
  }));
  const slots: Slot[] = [];
  for (const row of rows) {
    const last = slots[slots.length - 1];
    if (last && last.startsAt === row.session.startsAt) last.rows.push(row);
    else slots.push({ startsAt: row.session.startsAt, rows: [row] });
  }
  // The line for now goes above the first time that has not ended.
  const nowIndex = slots.findIndex((slot) => slot.rows.some((row) => Date.parse(row.session.endsAt) > now));

  function open(row: Row) {
    if (openId === row.session.id) return;
    setOpenId(row.session.id);
    setAttending(false);
    track("home_card_used", { card: "today", action: "open_session" });
  }
  function close() {
    setOpenId(null);
    setAttending(false);
  }
  const opened = openId ? rows.find((row) => row.session.id === openId) : undefined;

  return (
    <section aria-labelledby="today-title" className="overflow-hidden rounded-card border border-line bg-paper shadow-card">
      <div className="flex flex-wrap items-end justify-between gap-3 px-5 pt-4 pb-3">
        <div>
          <h2 id="today-title" className="text-[19px]">
            Sessions today
          </h2>
          <p className="text-[13.5px] text-ink-2">{formatDayLong(new Date(now).toISOString(), timezone)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {query.isError ? <TodayStale onRetry={() => void query.refetch()} /> : null}
          <TodayChips progress={rows.map((row) => row.progress)} />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              track("home_card_used", { card: "today", action: "open_calendar" });
              void navigate({ to: "/owner/schedule" });
            }}
          >
            Open in Calendar
          </Button>
        </div>
      </div>
      <ol aria-labelledby="today-title" className="border-t border-line pb-2">
        {slots.map((slot, index) => (
          <li key={slot.startsAt}>
            {index === nowIndex ? (
              <div className="flex items-center gap-2.5 px-5 pt-3" aria-label={`Now, ${formatTime(new Date(now).toISOString(), timezone)}`}>
                <span aria-hidden="true" className="size-2 rounded-full bg-accent" />
                <span className="text-[11.5px] font-bold tracking-[0.06em] text-accent-strong uppercase">
                  Now {formatTime(new Date(now).toISOString(), timezone)}
                </span>
                <span aria-hidden="true" className="h-0.5 flex-1 bg-accent/35" />
              </div>
            ) : null}
            <div className="flex flex-wrap gap-x-4 gap-y-2 px-5 py-2.5">
              <div className="w-20 shrink-0 pt-2">
                <p className="text-[15px] font-semibold tabular-nums">{formatTime(slot.startsAt, timezone)}</p>
                <p className="text-[12.5px] text-ink-2">{slotWhen(slot, now)}</p>
              </div>
              <ul className="flex min-w-0 flex-1 basis-[420px] flex-col gap-2">
                {slot.rows.map((row) => (
                  <TodayRow
                    key={row.session.id}
                    row={row}
                    timezone={timezone}
                    noStaff={`No ${words.staff}`}
                    photoOf={photoOf}
                    fillFirst={fillFirst}
                    onOpen={() => open(row)}
                  />
                ))}
              </ul>
            </div>
          </li>
        ))}
      </ol>
      {opened ? (
        <Panel open onClose={close} title={opened.session.groupName} header={false} flush>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <SessionPanelContent
              business={business}
              open={opened}
              owned={owned}
              editor={editor.data}
              attending={attending}
              onAttendingChange={setAttending}
              attendanceSource="home_today"
              onClose={close}
            />
          </div>
        </Panel>
      ) : null}
    </section>
  );
}

function TodayRow({
  row,
  timezone,
  noStaff,
  photoOf,
  fillFirst,
  onOpen,
}: {
  row: Row;
  timezone: string;
  noStaff: string;
  /** The coach's photo, for the face next to the name. */
  photoOf: (userId: string | null | undefined) => string | null;
  /** Where clients pick each class: the fill before attendance. */
  fillFirst: boolean;
  onOpen: () => void;
}) {
  const { session, progress } = row;
  const fill = sessionFill(session);
  const opens = progress.state !== "cancelled";
  const meter = progress.state === "cancelled" ? null : <FillMeter fill={fill} className="flex-1 basis-28 sm:w-[200px] sm:flex-none sm:basis-auto" />;
  // Taken is a full bar, also when only people with no booking came.
  const share = progress.state === "taken" ? 100 : progress.total > 0 ? (progress.checked / progress.total) * 100 : 0;
  // A later session's time is already the heading of its group; the bar shows once a mark is in.
  const status = progress.state === "later" ? null : todayStatusText(progress, session, timezone);
  return (
    // The level colour is a strip on the edge, inside the padding, so wrapped lines keep their margin on a phone.
    // The name is a button stretched over the whole row, so a click anywhere on the row opens the
    // session; the buttons on the row sit above it.
    <li className={`relative overflow-hidden rounded-xl border border-line bg-paper py-2.5 pr-3 pl-4 ${opens ? "hover:bg-surface" : ""}`}>
      <span aria-hidden="true" className="absolute inset-y-0 left-0 w-1.5" style={{ background: row.color }} />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1 basis-[180px]">
          {opens ? (
            <button
              type="button"
              className="text-left text-[15px] font-semibold text-ink after:absolute after:inset-0 after:content-[''] hover:underline"
              aria-label={`${session.groupName} ${formatTime(session.startsAt, timezone)}, open the session and its class list`}
              onClick={onOpen}
            >
              {session.groupName}
            </button>
          ) : (
            <p className="text-[15px] font-semibold text-ink-3 line-through">{session.groupName}</p>
          )}
          <p className="flex items-center gap-1 text-[13px] text-ink-2">
            {session.staffName ? <AvatarName name={session.staffName} photoUrl={photoOf(session.staffUserId)} size={18} /> : noStaff}
            {session.kind === "private" ? <span className="shrink-0">· Private</span> : null}
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-2 sm:w-auto sm:flex-nowrap sm:justify-end">
          {fillFirst ? meter : null}
          {status ? (
            <div className="flex min-w-0 flex-1 basis-28 flex-col gap-1.5 sm:w-[200px] sm:flex-none sm:basis-auto">
              <p className={`text-[13px] font-semibold ${TONE[progress.state]}`}>{status}</p>
              {progress.checked > 0 || progress.state === "taken" ? (
                <div aria-hidden="true" className="h-1.5 overflow-hidden rounded-full bg-sunken">
                  <div className={`h-full rounded-full ${progress.state === "taken" ? "bg-confirmed" : "bg-accent"}`} style={{ width: `${share}%` }} />
                </div>
              ) : null}
            </div>
          ) : null}
          {fillFirst ? null : meter}
          {canTakeAttendance(progress) ? (
            <Button
              size="sm"
              variant={URGENT.has(progress.state) ? "primary" : "secondary"}
              className="relative z-10 ml-auto shrink-0"
              onClick={onOpen}
              aria-label={`Open session, ${session.groupName} ${formatTime(session.startsAt, timezone)}`}
            >
              Open session
            </Button>
          ) : null}
        </div>
      </div>
    </li>
  );
}
